"use strict";

import Global from "/js/core/global";
import UserLesson from "/js/core/user-lesson";
import AppUtil from "/js/util/app-util";
import Util from "/js/util/util";

/**
 * Manages the user's lessons.
 * And automatically stores info in localStorage, and loads it on-init.
 */
export default class UserLessons {

  static #STARTED_SETS_KEY = "startedSets";

  /**
   * lesson id-to-UserLesson map.
   */
  static #map = null;
  static #startedSets = null;
  static #isLocalStorageEnabled = false;

  /**
   * Must be called first. Loads UserLesson's from local storage.
   * @param enableLocalStorageIfAvailable When false, will NOT load or save to local storage.
   *     (Not to be confused with whether the browser has allowed localStorage access or not)
   */
  static init(enableLocalStorageIfAvailable=false) {

    if (enableLocalStorageIfAvailable && !Util.isLocalStorageAvailable) {
      clw("WARNING: local storage is probably being blocked by the browser, will disable");
      enableLocalStorageIfAvailable = false;
    }
    this.#isLocalStorageEnabled = enableLocalStorageIfAvailable;

    this.#map = {};

    if (this.#isLocalStorageEnabled) {
      for (const key in localStorage) {
        if (!localStorage.hasOwnProperty(key) || (!key.startsWith("lesson-") && key !== this.#STARTED_SETS_KEY)) {
          continue;
        }
        const s = localStorage.getItem(key);
        let o;
        try {
          o = JSON.parse(s);
        } catch (e) {
          clw("ERROR: Couldn't parse value for", key, s);
          continue;
        }
        if (key === this.#STARTED_SETS_KEY) {
          this.#startedSets = o;
        } else {
          const userLesson = UserLesson.fromServerObject(o);
          if (!userLesson) {
            clw("ERROR: Couldn't deserialize string int UserLesson:", key, s);
            continue;
          }
          UserLessons.#map[userLesson.lessonId] = userLesson;
        }
      }
    }

    cl("UserLessons.init()", UserLessons.#map);
  }

  static get isLocalStorageEnabled() {
    return this.#isLocalStorageEnabled;
  }

  /**
   * Gets UserLesson by lessonId.
   */
  static getByLessonId(lessonId) {
    return UserLessons.#map[lessonId];
  }

  static get map() {
    return UserLessons.#map;
  }

  static add(userLesson) {
    UserLessons.#map[userLesson.lessonId] = userLesson;
  }

  /**
   * Convenience shortcut. Returns the UserLesson for  the current lesson.
   * Auto-adds userLesson if needed.
   */
  static get current() {
    let userLesson = UserLessons.#map[Global.lessonId];
    if (!userLesson) {
      cl("UserLessons - adding record");
      const totalCards = AppUtil.getNumCards(Global.lessonData);
      userLesson = new UserLesson(Global.lessonId, totalCards);
      UserLessons.#map[Global.lessonId] = userLesson;
    }
    return userLesson;
  }

  static get totalViewedLessonsCount() {
    let count = 0;
    const keys = Object.keys(this.#map);
    for (const key of keys) {
      if (this.#map[key].startedAt !== 0) {
        count++;
      }
    }
    return count;
  }

  static get totalCompletedLessonsCount() {
    let count = 0;
    const keys = Object.keys(this.#map);
    for (const key of keys) {
      if (this.#map[key].completedAt !== 0) {
        count++;
      }
    }
    return count;
  }

  /**
   * Convenience function
   */
  static saveCurrent() {
    UserLessons.saveByLessonId(Global.lessonId, true);
  }

  static isComplete(lessonId) {
    if (UserLessons.#map[lessonId]) {
      return UserLessons.#map[lessonId].isComplete;
    }
    return false;
  }

  /**
   * Clears the user lesson data.
   */
  static clear() {
    UserLessons.#map = {};
    UserLessons.#startedSets = [];
  }

  /**
   * Clears the user lesson data AND the backing local storage data.
   * Used for development.
   */
  static erase() {

    UserLessons.clear();

    if (!Util.isLocalStorageAvailable) {
      clw("erase() - local storage is not enabled. ignoring.");
      return;
    }

    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith("lesson-")) {
        localStorage.removeItem(key);
      }
    }
    localStorage.removeItem(this.#STARTED_SETS_KEY);
  }

  /**
   * Development convenience
   */
  static setAllLessonsInMinicourseToComplete(exceptOne) {
    if (!Global.minicourseData) {
      return;
    }
    for (const lessonId of Global.minicourseData.lessonIds) {
      let userLesson = UserLessons.#map[lessonId];
      if (userLesson && userLesson.isComplete) {
        continue;
      }
      if (exceptOne) {
        exceptOne = false;
        continue;
      }
      if (!userLesson) {
        userLesson = new UserLesson(lessonId, 50);  // TODO: "50"
        UserLessons.add(userLesson);
      }
      userLesson.setToComplete();
      UserLessons.saveByLessonId(lessonId, true);
    }
    UserLessons.saveStartedSet(Global.minicourseData.id);
  }

  /**
   * Serializes UserLesson to localStorage.
   */
  static saveByLessonId(lessonId, shouldTouch=true) {
    if (!this.#isLocalStorageEnabled) {
      return;
    }
    const userLesson = UserLessons.#map[lessonId];
    if (!userLesson) {
      clw("WARNING: UserLesson doesn't exist:", lessonId);
      return;
    }
    if (shouldTouch) {
      userLesson.touch();
    }
    try {
      const s = JSON.stringify(userLesson.asServerObject());
      localStorage.setItem(lessonId, s);
      // cl("saveByLessonId()", lessonId, s);
    } catch (e) {
      clw("WARNING: Couldn't save UserLesson", lessonId);
    }
  }

  /**
   * Serializes startedSets to localStorage.
   */
  static saveStartedSet(setId) {
    if (!this.#isLocalStorageEnabled) {
      return false;
    }

    if (this.#startedSets) {
      if (this.#startedSets.includes(setId)) {
        return false;
      } else {
        this.#startedSets.push(setId);
      }
    } else {
      this.#startedSets = [setId];
    }

    try {
      const startedSetsString = JSON.stringify(this.#startedSets);
      localStorage.setItem(this.#STARTED_SETS_KEY, startedSetsString);
      // cl("saveStartedSet()", this.#startedSetsKey, startedSetsString);
    } catch (e) {
      clw("WARNING: Couldn't save setId to startedSets", setId);
      return false;
    }
    return true;
  }

  /**
   * For debugging.
   */
  static printLocalStorage() {
    if (!this.#isLocalStorageEnabled) {
      return;
    }
    let s = "\r\n";
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      s += key + "\r\n";
      s += localStorage[key] + "\r\n";
    }
    cl(s);
  }
}

// Dev convenience:
if (! window.Primer) {
  window.Primer = {};
}
window.Primer.UserLessons = UserLessons;
